import { useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  formatMarketPrice,
  formatMonthTick,
  getPeakAndFloor,
} from "../utils/marketPriceHistory";

const VIEW_WIDTH = 320;
const PLOT_TOP = 22;
const PLOT_HEIGHT = 112;
const PLOT_BOTTOM = PLOT_TOP + PLOT_HEIGHT;
const PAD_LEFT = 22;
const PAD_RIGHT = 14;
const VIEW_HEIGHT = 180;
const TICK_Y = 174;
const PEAK_LABEL_Y = 12;
const FLOOR_LABEL_GAP = 14;

function numericPrice(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function plotX(index, count) {
  if (count <= 1) return (PAD_LEFT + VIEW_WIDTH - PAD_RIGHT) / 2;
  return (
    PAD_LEFT + (index * (VIEW_WIDTH - PAD_LEFT - PAD_RIGHT)) / (count - 1)
  );
}

function plotY(price, minPrice, maxPrice) {
  if (maxPrice === minPrice) {
    return PLOT_BOTTOM;
  }
  return PLOT_TOP + ((maxPrice - price) / (maxPrice - minPrice)) * PLOT_HEIGHT;
}

function floorAnnotation(floorY) {
  const labelY = floorY + FLOOR_LABEL_GAP;
  if (labelY <= PLOT_BOTTOM - 2) {
    return {
      labelY,
      stemY2: floorY + 10,
      showStem: true,
      sideNudge: 0,
    };
  }
  return {
    labelY: Math.min(floorY + 4, PLOT_BOTTOM - 1),
    stemY2: floorY,
    showStem: false,
    sideNudge: 1,
  };
}

function textAnchorFor(index, count, nudge) {
  if (nudge < 0) return "end";
  if (nudge > 0) return "start";
  const t = count <= 1 ? 0.5 : index / (count - 1);
  if (t <= 0.12) return "start";
  if (t >= 0.88) return "end";
  return "middle";
}

function labelNudge(peak, floor) {
  if (!peak || !floor) return { peak: 0, floor: 0 };
  if (Math.abs(peak.index - floor.index) > 1) return { peak: 0, floor: 0 };
  if (peak.index === floor.index) {
    return { peak: -1, floor: 1 };
  }
  if (peak.index < floor.index) {
    return { peak: -1, floor: 1 };
  }
  return { peak: 1, floor: -1 };
}

function lineSegments(points) {
  const segments = [];
  let current = [];
  points.forEach((point) => {
    if (point.price == null) {
      if (current.length) {
        segments.push(current);
        current = [];
      }
      return;
    }
    current.push(point);
  });
  if (current.length) segments.push(current);
  return segments;
}

function pointsToPolyline(points) {
  return points.map((point) => `${point.x},${point.y}`).join(" ");
}

function pointsToArea(points) {
  if (points.length < 2) return "";
  const first = points[0];
  const last = points[points.length - 1];
  return `${pointsToPolyline(points)} ${last.x},${PLOT_BOTTOM} ${first.x},${PLOT_BOTTOM}`;
}

export default function MarketPriceHistoryChart({
  series = [],
  itemLabel = "Item",
}) {
  const theme = useTheme();
  const lineColor = theme.palette.header?.main || theme.palette.primary.main;
  const tickColor = theme.palette.text.secondary;
  const labelColor = theme.palette.text.primary;

  const chart = useMemo(() => {
    const { peak, floor } = getPeakAndFloor(series);
    if (!peak || !floor) {
      return null;
    }

    const count = series.length;
    const minPrice = Math.min(0, floor.price);
    const maxPrice = Math.max(0, peak.price);
    const points = series.map((point, index) => {
      const price = numericPrice(point.price);
      return {
        month: point.month,
        price,
        index,
        x: plotX(index, count),
        y: price == null ? null : plotY(price, minPrice, maxPrice),
      };
    });

    const peakPoint = points[peak.index];
    const floorPoint = points[floor.index];
    const floorCallout = floorAnnotation(floorPoint.y);
    const nudge = labelNudge(peak, floor);
    if (!floorCallout.showStem && nudge.floor === 0) {
      nudge.floor =
        floor.index <= (count - 1) / 2
          ? floorCallout.sideNudge
          : -floorCallout.sideNudge;
    }
    const tickIndexes = [0, Math.floor((count - 1) / 2), count - 1].filter(
      (index, i, arr) => arr.indexOf(index) === i
    );

    return {
      peak,
      floor,
      peakPoint,
      floorPoint,
      floorCallout,
      nudge,
      segments: lineSegments(points),
      ticks: tickIndexes.map((index) => ({
        index,
        x: plotX(index, count),
        label: formatMonthTick(series[index].month),
      })),
    };
  }, [series]);

  if (!chart) {
    return (
      <Box
        sx={{
          height: VIEW_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          No price history
        </Typography>
      </Box>
    );
  }

  const peakLabel = `Peak ${formatMarketPrice(chart.peak.price)}`;
  const floorLabel = `Floor ${formatMarketPrice(chart.floor.price)}`;
  const ariaLabel = `${itemLabel} 12-month average price, ${peakLabel}, ${floorLabel}`;

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      role="img"
      aria-label={ariaLabel}
      sx={{ display: "block", width: "100%", height: "auto" }}
    >
      {chart.segments.map((segment, index) =>
        segment.length >= 2 ? (
          <polygon
            key={`fill-${index}`}
            points={pointsToArea(segment)}
            fill={alpha(lineColor, 0.12)}
            stroke="none"
          />
        ) : null
      )}
      {chart.segments.map((segment, index) =>
        segment.length >= 2 ? (
          <polyline
            key={`line-${index}`}
            points={pointsToPolyline(segment)}
            fill="none"
            stroke={lineColor}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ) : segment.length === 1 ? (
          <circle
            key={`dot-${index}`}
            cx={segment[0].x}
            cy={segment[0].y}
            r="2.5"
            fill={lineColor}
          />
        ) : null
      )}

      <line
        x1={4}
        y1={PLOT_BOTTOM}
        x2={VIEW_WIDTH - PAD_RIGHT}
        y2={PLOT_BOTTOM}
        stroke={theme.palette.divider}
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      <text
        x={4}
        y={PLOT_BOTTOM + 12}
        textAnchor="start"
        fill={tickColor}
        fontSize="10"
        fontFamily={theme.typography.fontFamily}
        data-testid="price-zero-tick"
      >
        0
      </text>

      <line
        x1={chart.peakPoint.x}
        y1={chart.peakPoint.y}
        x2={chart.peakPoint.x}
        y2={PEAK_LABEL_Y + 2}
        stroke={labelColor}
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      <text
        x={chart.peakPoint.x}
        y={PEAK_LABEL_Y}
        textAnchor={textAnchorFor(
          chart.peak.index,
          series.length,
          chart.nudge.peak
        )}
        fill={labelColor}
        fontSize="11"
        fontWeight="600"
        fontFamily={theme.typography.fontFamily}
      >
        {peakLabel}
      </text>

      {chart.floorCallout.showStem ? (
        <line
          x1={chart.floorPoint.x}
          y1={chart.floorPoint.y}
          x2={chart.floorPoint.x}
          y2={chart.floorCallout.stemY2}
          stroke={labelColor}
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
      <text
        x={chart.floorPoint.x}
        y={chart.floorCallout.labelY}
        textAnchor={textAnchorFor(
          chart.floor.index,
          series.length,
          chart.nudge.floor
        )}
        fill={labelColor}
        fontSize="11"
        fontWeight="600"
        fontFamily={theme.typography.fontFamily}
      >
        {floorLabel}
      </text>

      <circle
        cx={chart.peakPoint.x}
        cy={chart.peakPoint.y}
        r="3"
        fill={lineColor}
      />
      <circle
        cx={chart.floorPoint.x}
        cy={chart.floorPoint.y}
        r="3"
        fill={lineColor}
      />

      {chart.ticks.map((tick) => (
        <text
          key={`tick-${tick.index}`}
          x={tick.x}
          y={TICK_Y}
          textAnchor={textAnchorFor(tick.index, series.length, 0)}
          fill={tickColor}
          fontSize="10"
          fontFamily={theme.typography.fontFamily}
        >
          {tick.label}
        </text>
      ))}
    </Box>
  );
}
